import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Send, Plus, Trash2, Bot, User, ChevronRight, Loader } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// ── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser ? "bg-brand-orange" : "bg-brand-green"
      }`} aria-hidden="true">
        {isUser ? <User size={14} className="text-white" /> : <Bot size={14} className="text-brand-navy" />}
      </div>
      {/* Content */}
      <div className={`max-w-[78%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-brand-orange text-white rounded-tr-sm"
            : "bg-[#1E2436] text-gray-100 border border-white/5 rounded-tl-sm"
        } dark-section`}>
          {msg.content}
          {msg.streaming && (
            <span className="inline-flex gap-0.5 ml-1 align-middle" aria-label="Typing">
              <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </span>
          )}
        </div>
        {msg.created_at && (
          <time className="text-xs text-gray-500 px-1">{formatTime(msg.created_at)}</time>
        )}
      </div>
    </div>
  );
}

// ── Sidebar session item ─────────────────────────────────────────────────────

function SessionItem({ session, active, onClick, onDelete }) {
  return (
    <div
      className={`group flex items-start gap-2 p-3 rounded-xl cursor-pointer transition-colors ${
        active ? "bg-brand-green/10 border border-brand-green/30" : "hover:bg-white/5"
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      aria-current={active ? "true" : undefined}
      data-testid="concierge-session-item"
    >
      <Bot size={14} className={`mt-0.5 flex-shrink-0 ${active ? "text-brand-green" : "text-gray-500"}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-300 truncate">{session.preview || "New conversation"}</p>
        <p className="text-xs text-gray-500 mt-0.5">{session.message_count} msg{session.message_count !== 1 ? "s" : ""}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(session.session_id); }}
        className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-all"
        aria-label="Delete conversation"
        data-testid="delete-concierge-session"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

// ── Starter prompts ──────────────────────────────────────────────────────────

const STARTERS = [
  "Help me find a tennis partner in Fort Worth",
  "I'm an intermediate pickleball player, who should I play with?",
  "Find me a golf partner available on weekends",
  "What skill level should I look for as a beginner?",
];

// ── Main page ────────────────────────────────────────────────────────────────

export default function ConciergePage() {
  const { user } = useAuth();
  const showToast = useToast();

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // ── Fetch past sessions ────────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    try {
      const r = await fetch(`${API}/concierge/sessions`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setSessions(data.sessions || []);
      }
    } catch {
      /* silent */
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ── Load session messages ──────────────────────────────────────────────────
  const openSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    try {
      const r = await fetch(`${API}/concierge/sessions/${sessionId}`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setMessages(data.messages || []);
      }
    } catch {
      setMessages([]);
    }
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  // ── New conversation ───────────────────────────────────────────────────────
  const newChat = () => {
    setActiveSessionId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  // ── Delete session ─────────────────────────────────────────────────────────
  const deleteSession = async (sessionId) => {
    try {
      await fetch(`${API}/concierge/sessions/${sessionId}`, {
        method: "DELETE",
        credentials: "include",
      });
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch {
      showToast("Failed to delete conversation");
    }
  };

  // ── Send message (SSE streaming) ───────────────────────────────────────────
  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setInput("");
    setSending(true);

    // Optimistically add user message
    const userMsg = { role: "user", content: trimmed, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);

    // Scroll to bottom immediately after adding user message
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 20);

    // Placeholder assistant message while streaming
    const placeholder = { role: "assistant", content: "", streaming: true, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, placeholder]);

    try {
      const response = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: trimmed, session_id: activeSessionId }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";
      let newSessionId = activeSessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (!raw) continue;
          try {
            const chunk = JSON.parse(raw);
            if (chunk.session_id) {
              newSessionId = chunk.session_id;
              setActiveSessionId(chunk.session_id);
            } else if (chunk.text) {
              fullText += chunk.text;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { ...next[next.length - 1], content: fullText };
                return next;
              });
            } else if (chunk.done) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { ...next[next.length - 1], streaming: false };
                return next;
              });
            } else if (chunk.error) {
              throw new Error(chunk.error);
            }
          } catch {
            /* skip malformed chunk */
          }
        }
      }

      // Refresh sidebar session list
      await loadSessions();
      if (newSessionId && newSessionId !== activeSessionId) {
        // Will be set already via setActiveSessionId above
      }
    } catch (err) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          ...next[next.length - 1],
          content: "Sorry, I couldn't process that. Please try again.",
          streaming: false,
        };
        return next;
      });
      showToast("Concierge error — please try again");
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Keyboard submit ────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="min-h-screen bg-[#0F1629] flex flex-col pt-16 dark-section" data-testid="concierge-page">
      {/* Skip nav landmark */}
      <div className="flex flex-1 overflow-hidden" style={{ height: "calc(100vh - 64px)" }}>

        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <aside
          className={`${sidebarOpen ? "w-72" : "w-0 overflow-hidden"} flex-shrink-0 border-r border-white/8 bg-[#0C1020] flex flex-col transition-all duration-200 dark-section`}
          aria-label="Conversation history"
        >
          <div className="p-4 border-b border-white/8 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-white">AI Concierge</h2>
              <p className="text-xs text-gray-500 mt-0.5">Partner matching assistant</p>
            </div>
            <button
              onClick={newChat}
              className="p-2 rounded-lg bg-brand-green/10 border border-brand-green/20 text-brand-green hover:bg-brand-green/20 transition-colors"
              aria-label="Start new conversation"
              data-testid="new-concierge-chat-btn"
            >
              <Plus size={15} />
            </button>
          </div>

          {/* Session list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1" role="list" aria-label="Past conversations">
            {loadingSessions ? (
              <div className="flex justify-center py-8">
                <Loader size={18} className="text-gray-500 animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-xs text-gray-600 text-center py-8 px-2">No conversations yet.<br />Start one below.</p>
            ) : (
              sessions.map((s) => (
                <SessionItem
                  key={s.session_id}
                  session={s}
                  active={s.session_id === activeSessionId}
                  onClick={() => openSession(s.session_id)}
                  onDelete={deleteSession}
                />
              ))
            )}
          </div>

          {/* Sidebar footer */}
          <div className="p-4 border-t border-white/8">
            <Link
              to="/find-partners"
              className="flex items-center gap-2 text-xs text-brand-green hover:underline"
              data-testid="concierge-find-partners-link"
            >
              Browse partners manually <ChevronRight size={12} />
            </Link>
          </div>
        </aside>

        {/* ── Main chat area ────────────────────────────────────────────────── */}
        <main className="flex-1 flex flex-col overflow-hidden" id="main-content">

          {/* Top bar */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/8 bg-[#0C1020] dark-section">
            {/* Toggle sidebar on mobile */}
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="text-gray-400 hover:text-white transition-colors md:hidden"
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              aria-expanded={sidebarOpen}
              data-testid="sidebar-toggle-btn"
            >
              <Bot size={18} />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-brand-green flex items-center justify-center" aria-hidden="true">
                <Bot size={14} className="text-brand-navy" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white leading-none">ActivitySports Concierge</p>
                <p className="text-xs text-brand-green leading-none mt-0.5">Powered by Gemini AI</p>
              </div>
            </div>
            {!isEmpty && (
              <button
                onClick={newChat}
                className="ml-auto text-xs text-gray-400 hover:text-white border border-white/10 rounded-lg px-3 py-1.5 hover:border-white/20 transition-colors"
                data-testid="new-chat-top-btn"
              >
                New chat
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-5" role="log" aria-live="polite" aria-label="Chat messages">
            {isEmpty ? (
              /* Welcome screen */
              <div className="max-w-xl mx-auto text-center pt-12 pb-4">
                <div className="w-16 h-16 rounded-2xl bg-brand-green/10 border border-brand-green/20 flex items-center justify-center mx-auto mb-5">
                  <Bot size={30} className="text-brand-green" aria-hidden="true" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Find Your Perfect Partner</h1>
                <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
                  Tell me your sport, skill level, and schedule — I'll help you find the best match.
                </p>
                {/* Starter prompts */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      className="p-3.5 rounded-xl bg-white/4 border border-white/8 text-left text-sm text-gray-300 hover:bg-white/8 hover:border-brand-green/30 hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-brand-green/40"
                      data-testid="starter-prompt-btn"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)
            )}
            <div ref={bottomRef} aria-hidden="true" />
          </div>

          {/* Input area */}
          <div className="px-4 py-4 border-t border-white/8 bg-[#0C1020] dark-section">
            <div className="max-w-3xl mx-auto">
              <div className="flex gap-3 items-end">
                <label htmlFor="concierge-input" className="sr-only">Message the AI Concierge</label>
                <textarea
                  id="concierge-input"
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  placeholder="Ask about partners, sports, or scheduling…"
                  className="flex-1 resize-none bg-[#1A2035] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green/40 transition-colors max-h-32 overflow-auto dark-section"
                  disabled={sending}
                  aria-label="Type your message"
                  data-testid="concierge-input"
                  style={{ minHeight: "48px" }}
                  onInput={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
                  }}
                />
                <button
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || sending}
                  className="flex-shrink-0 w-11 h-11 rounded-xl bg-brand-green flex items-center justify-center text-brand-navy hover:bg-brand-green/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-green/50"
                  aria-label="Send message"
                  data-testid="concierge-send-btn"
                >
                  {sending ? <Loader size={17} className="animate-spin" /> : <Send size={17} />}
                </button>
              </div>
              <p className="text-xs text-gray-600 text-center mt-2">
                Press Enter to send · Shift+Enter for new line
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
